/**
 * Custom hook for handling modal overlay close behavior.
 *
 * Ensures the modal only closes when the user clicks on the overlay itself,
 * not when they start a click inside the modal content and drag to the overlay
 * (e.g., selecting text). This prevents accidental closes.
 *
 * @param {Function} onClose - Callback to close the modal
 * @returns {{ onMouseDown: Function, onClick: Function }} Event handlers for the overlay element
 *
 * @example
 * const overlayHandlers = useModalOverlayClose(() => setShowModal(false));
 * return (
 *   <div className="modal-overlay" {...overlayHandlers}>
 *     <div className="modal-content" onClick={(e) => e.stopPropagation()}>
 *       ...
 *     </div>
 *   </div>
 * );
 */

import { useCallback, useRef } from 'react';

const useModalOverlayClose = (onClose) => {
  const mouseDownOnOverlay = useRef(false);

  const onMouseDown = useCallback((e) => {
    mouseDownOnOverlay.current = e.target.className === 'modal-overlay';
  }, []);

  const onClick = useCallback((e) => {
    if (e.target.className === 'modal-overlay' && mouseDownOnOverlay.current) {
      onClose();
    }
    mouseDownOnOverlay.current = false;
  }, [onClose]);

  return { onMouseDown, onClick };
};

export default useModalOverlayClose;
